import React, { useCallback, useRef } from 'react';
import { message } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../Constants';
import GateManagement from './GateManagement';

// Only the fields an admin can edit; zone ids are derived by the server, so they are ignored when diffing.
const norm = (v) => (v === null || v === undefined ? '' : v);
const gateKey = (g) => {
  const scope = g.gateScope || (g.parentZoneId ? 'session' : 'main');
  return JSON.stringify([
    norm(g.gateName), norm(g.gateType), scope,
    scope === 'session' ? norm(g.zoneName) : '',
    norm(g.location), norm(g.description), norm(g.capacity), g.isActive !== false,
  ]);
};

const toRequest = (g) => {
  const scope = g.gateScope || (g.parentZoneId ? 'session' : 'main');
  return {
    gateName: g.gateName,
    gateType: g.gateType,
    gateScope: scope,
    zoneName: scope === 'session' ? g.zoneName : null,
    location: g.location || null,
    description: g.description || null,
    capacity: g.capacity ?? null,
    isActive: g.isActive !== false,
  };
};

/**
 * Standalone gate manager: reuses GateManagement but saves each add/edit/delete straight to the
 * per-gate endpoints (the event edit form instead re-sends the whole event).
 */
const EventGatesPanel = ({ eventId, gates, setGates, reload }) => {
  const { t } = useTranslation();
  const gatesRef = useRef(gates);
  gatesRef.current = gates;
  const busyRef = useRef(false);

  const handleChange = useCallback(async (next) => {
    const prev = gatesRef.current;
    const prevById = new Map(prev.map((g) => [g.gateId, g]));
    const nextById = new Map(next.map((g) => [g.gateId, g]));

    const added = next.filter((g) => !prevById.has(g.gateId));
    const removed = prev.filter((g) => !nextById.has(g.gateId));
    const changed = next.filter((g) => prevById.has(g.gateId) && gateKey(g) !== gateKey(prevById.get(g.gateId)));

    // GateManagement also emits zone-id normalisation with no real edit: just keep it locally.
    if (!added.length && !removed.length && !changed.length) {
      setGates(next);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setGates(next);

    const base = `${BASE_URL}/Organizer/events/${eventId}/gates`;
    try {
      for (const g of added) await axios.post(base, toRequest(g));
      for (const g of changed) await axios.put(`${base}/${g.gateId}`, toRequest(g));
      for (const g of removed) {
        const res = await axios.delete(`${base}/${g.gateId}`);
        if (res.data?.result === 'deactivated') {
          message.info(t('organizerManagement.page.gateDeactivated'));
        }
      }
    } catch (error) {
      console.error('Error saving gates:', error);
      message.error(error.response?.data?.message || t('organizerManagement.page.gatesSaveFailed'));
    } finally {
      busyRef.current = false;
      // Re-read so server-assigned gate ids and any rejected change are reflected.
      await reload();
    }
  }, [eventId, setGates, reload, t]);

  return <GateManagement eventId={eventId} gates={gates} onChange={handleChange} />;
};

export default EventGatesPanel;
